import React, { useState } from 'react';
import { Table, Button, Pagination, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ClientSugSelector } from '../../../../components/settlement/ClientSugSelector';
import { formatCentsAmount } from '../../../../utils/invoiceAmount';
import {
    GET_REALTIME_INVOICE_CLIENTS,
    GetRealtimeInvoiceClientsResponse,
} from './graphql/queries/getRealtimeInvoiceClients';
import { RealtimeInvoiceClientType } from './graphql/types/RealtimeInvoiceType';

/**
 * 实时结算 Invoice 客户列表页：顶部按结算客户下拉过滤，列表展示客户 ID / 名称 / 可开票金额 / 操作。
 * 点击 Detail 新开浏览器 Tab 进入该客户的详情页。
 */
const RealtimeInvoiceClientListPage: React.FC = () => {
    const { t } = useTranslation();

    const [tempClientId, setTempClientId] = useState<string | undefined>(undefined);
    const [clientId, setClientId] = useState<string | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);

    const listQuery = useQuery<GetRealtimeInvoiceClientsResponse>(GET_REALTIME_INVOICE_CLIENTS, {
        variables: { settlementClientId: clientId ?? null, page, size },
        fetchPolicy: 'cache-and-network',
    });
    const result = listQuery.data?.getRealtimeInvoiceClients;
    const rows = result?.content ?? [];
    const total = result?.pageInfo?.totalElements ?? 0;

    const handleSearch = () => {
        setClientId(tempClientId);
        setPage(1);
    };

    const handleReset = () => {
        setTempClientId(undefined);
        setClientId(undefined);
        setPage(1);
    };

    const handleDetail = (r: RealtimeInvoiceClientType) => {
        const query = r.customerName ? `?name=${encodeURIComponent(r.customerName)}` : '';
        window.open(`/settlement/account/realtime-invoice/${r.settlementClientId}${query}`, '_blank');
    };

    const columns: ColumnsType<RealtimeInvoiceClientType> = [
        { title: t('settlement.realtimeInvoice.clientList.customerId'), dataIndex: 'channelId', render: (v: string | null) => v || '-' },
        { title: t('settlement.realtimeInvoice.clientList.customerName'), dataIndex: 'customerName', render: (v: string | null) => v || '-' },
        {
            title: t('settlement.realtimeInvoice.clientList.availableAmount'),
            dataIndex: 'availableInvoiceAmount',
            render: (v: number | null, r) => (v == null ? '—' : formatCentsAmount(v, r.currency)),
        },
        {
            title: t('settlement.realtimeInvoice.clientList.operation'),
            key: 'operation',
            render: (_: unknown, r) => (
                <Button type="link" size="small" onClick={() => handleDetail(r)}>
                    {t('settlement.realtimeInvoice.clientList.detail')} →
                </Button>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-4 flex items-center gap-2">
                <ClientSugSelector
                    value={tempClientId}
                    onChange={(v) => setTempClientId(typeof v === 'string' ? v : v?.[0])}
                    roleType={0}
                    className="w-[20em]"
                />
                <Space>
                    <Button type="primary" onClick={handleSearch}>
                        {t('settlement.realtimeInvoice.actions.filter')}
                    </Button>
                    <Button onClick={handleReset}>
                        {t('settlement.realtimeInvoice.clientList.reset')}
                    </Button>
                </Space>
            </div>

            <Table
                rowKey="settlementClientId"
                columns={columns}
                dataSource={rows}
                loading={listQuery.loading}
                pagination={false}
            />

            <div className="mt-4 flex items-center justify-between">
                <div className="text-gray-500">{total} {t('settlement.realtimeInvoice.results')}</div>
                <Pagination
                    current={page}
                    total={total}
                    pageSize={size}
                    size="small"
                    showSizeChanger
                    onChange={(p, s) => {
                        setPage(p);
                        setSize(s);
                    }}
                />
            </div>
        </div>
    );
};

export default RealtimeInvoiceClientListPage;
